// The accessibility seal of a website, in four steps: check the requirements, activate it, put it in the footer and
// verify it and keep it current. The seal is a round badge (an SVG served by SiteNorma) that links to a public
// verification card; it says what was checked and when, and it is not a WCAG accreditation.
import { CheckCircledIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  can,
  changeSeal,
  meQuery,
  SEAL_LANGS,
  type SealCriterionId,
  type SealLang,
  type SealSize,
  type SealState,
  type SealTheme,
  type SealView,
  sealBadgeUrl,
  sealQuery,
  siteQuery,
} from '../api';
import { announce, Button, ConfirmButton, Fold, Message, usePageTitle } from '../ui';
import { useDate } from './scan-parts';
import { Snippet } from './statement';

const SIZES: { id: SealSize; px: number }[] = [
  { id: 's', px: 120 },
  { id: 'm', px: 160 },
  { id: 'l', px: 200 },
];
const THEMES: SealTheme[] = ['light', 'dark'];
const STATES: SealState[] = ['vigente', 'caducado', 'suspendido'];

/** One requirement: its own row, a green check when met and a warning when pending, and the way to solve it. */
function Requirement({
  id,
  met,
  siteId,
  view,
  recommended = false,
}: {
  id: SealCriterionId | 'statement';
  met: boolean;
  siteId: string;
  view: SealView;
  recommended?: boolean;
}) {
  const { t } = useTranslation();
  const fix =
    id === 'ownership' ? (
      <Link to="/sites/$siteId/verificacion" params={{ siteId }}>
        {t('seal.fix.ownership')}
      </Link>
    ) : id === 'statement' ? (
      <Link to="/sites/$siteId/declaracion" params={{ siteId }}>
        {t('seal.fix.statement')}
      </Link>
    ) : id === 'analysis' ? (
      <Link to="/sites/$siteId" params={{ siteId }} search={{ preparar: true }}>
        {t('seal.fix.analysis')}
      </Link>
    ) : (
      <Link to="/sites/$siteId" params={{ siteId }}>
        {t(`seal.fix.${id}`)}
      </Link>
    );
  return (
    <li className="seal-requirement" data-met={met ? 'yes' : 'no'}>
      {met ? (
        <CheckCircledIcon className="seal-requirement-icon" aria-hidden="true" />
      ) : (
        <ExclamationTriangleIcon className="seal-requirement-icon" aria-hidden="true" />
      )}
      <span className="seal-requirement-text">
        <span>
          {t(recommended ? `seal.recommended.${id}` : `seal.criteria.${id}`, {
            days: view.rules.validDays,
          })}
        </span>
        <span className="sr-only"> ({t(met ? 'seal.requirements.met' : 'seal.requirements.pending')})</span>
      </span>
      {!met && <span className="seal-requirement-fix">{fix}</span>}
    </li>
  );
}

/** The seal as the visitor will see it: the image served by SiteNorma, on the footer's background. */
function Badge({
  siteId,
  lang,
  theme,
  size,
  state,
  alt,
}: {
  siteId: string;
  lang?: SealLang;
  theme: SealTheme;
  size: SealSize;
  state?: SealState;
  alt: string;
}) {
  const px = SIZES.find((entry) => entry.id === size)?.px ?? 160;
  return (
    <img
      src={sealBadgeUrl(siteId, { lang, theme, size, state })}
      width={px}
      height={px}
      alt={alt}
      className="seal-badge"
    />
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { id: T; label: string; lang?: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="seal-control">
      <span className="font-bold" id={`seal-${label}`}>
        {label}
      </span>
      <ToggleButtonGroup
        aria-labelledby={`seal-${label}`}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[value]}
        onSelectionChange={(keys) => {
          const next = [...keys][0];
          if (next) onChange(next as T);
        }}
        className="filter-chips"
      >
        {options.map((option) => (
          <ToggleButton key={option.id} id={option.id} className="filter-chip">
            <span lang={option.lang}>{option.label}</span>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
}

export function SealTab() {
  const { t, i18n } = useTranslation();
  const date = useDate();
  const { siteId } = useParams({ from: '/sites/$siteId' });
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const site = useQuery(siteQuery(siteId)).data;
  const [lang, setLang] = useState<SealLang | undefined>(undefined);
  const [theme, setTheme] = useState<SealTheme>('light');
  const [size, setSize] = useState<SealSize>('m');
  const seal = useQuery({ ...sealQuery(siteId, { lang, theme, size }), placeholderData: keepPreviousData });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  usePageTitle(t('seal.tab'), site?.name);
  const canEdit = can(me, 'site', 'update');
  const view = seal.data;
  const selected = lang ?? view?.languages.selected;

  const change = async (action: 'activate' | 'withdraw') => {
    setBusy(true);
    setError(null);
    setDone('');
    try {
      await changeSeal(siteId, action);
      await queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'seal'] });
      setDone(t(action === 'activate' ? 'seal.activate.done' : 'seal.withdrawn'));
      announce(t(action === 'activate' ? 'seal.activate.done' : 'seal.withdrawn'));
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 422 ? t('seal.notEligible') : t('errors.generic'),
      );
      await queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'seal'] });
    } finally {
      setBusy(false);
    }
  };

  const unmet = view?.criteria.filter((criterion) => !criterion.met) ?? [];
  const metCount = (view?.criteria.length ?? 0) - unmet.length;
  const total = view?.criteria.length ?? 0;
  const alt = t('seal.preview.alt');
  const pageLang = (SEAL_LANGS as readonly string[]).includes(i18n.language) ? i18n.language : 'es';

  return (
    <section aria-labelledby="sello" className="statement-tab seal-tab">
      <h2 id="sello" className="text-2xl font-bold">
        {t('seal.title')}
      </h2>
      <p className="max-w-3xl">{t('seal.intro')}</p>
      {seal.isError && <Message tone="error">{t('errors.generic')}</Message>}
      {error && <Message tone="error">{error}</Message>}
      <p role="status" className="font-bold text-ok empty:hidden">
        {done && (
          <span className="inline-flex items-center gap-1">
            <CheckCircledIcon aria-hidden="true" /> {done}
          </span>
        )}
      </p>
      {view && (
        <>
          <p className="seal-status" data-status={view.status}>
            <strong>{t(`seal.status.${view.status}`)}</strong>
            <span className="text-ink-muted">
              {` · ${t(`seal.statusHint.${view.status}`, { days: view.rules.validDays })}`}
            </span>
          </p>

          <section aria-labelledby="sello-requisitos" className="statement-step">
            <h3 id="sello-requisitos">{t('seal.requirements.title')}</h3>
            <p className="seal-progress">
              <strong>{t('seal.requirements.progress', { done: metCount, total })}</strong>
            </p>
            <progress
              className="seal-bar"
              value={metCount}
              max={total}
              aria-label={t('seal.requirements.progress', { done: metCount, total })}
            />
            <ul className="seal-requirements">
              {view.criteria.map((criterion) => (
                <Requirement
                  key={criterion.id}
                  id={criterion.id}
                  met={criterion.met}
                  siteId={siteId}
                  view={view}
                />
              ))}
            </ul>
            <div>
              <p className="text-sm font-bold">{t('seal.requirements.recommended')}</p>
              <ul className="seal-requirements">
                {view.recommended.map((entry) => (
                  <Requirement
                    key={entry.id}
                    id={entry.id}
                    met={entry.met}
                    siteId={siteId}
                    view={view}
                    recommended
                  />
                ))}
              </ul>
            </div>
            {view.evidence.scanDate && (
              <p className="text-sm text-ink-muted">
                {t('seal.requirements.lastAnalysis', {
                  date: date(view.evidence.scanDate),
                  pages: view.evidence.pagesAnalysed,
                })}
              </p>
            )}
            <Fold title={t('seal.scope.title')} hint={t('seal.scope.hint')} light>
              <ul className="list-disc pl-5">
                {(['analysis', 'checks', 'manual', 'not'] as const).map((key) => (
                  <li key={key}>{t(`seal.scope.${key}`, { days: view.rules.validDays })}</li>
                ))}
              </ul>
            </Fold>
          </section>

          <section aria-labelledby="sello-activar" className="statement-step">
            <h3 id="sello-activar">{t('seal.activate.title')}</h3>
            <p>{t('seal.activate.intro')}</p>
            {canEdit ? (
              <div className="flex flex-wrap items-center gap-3">
                {!view.active && (
                  <Button
                    onPress={() => change('activate')}
                    isDisabled={busy || !view.eligible}
                    aria-describedby={!view.eligible ? 'seal-why' : undefined}
                  >
                    {t('seal.activate.button')}
                  </Button>
                )}
                {!view.active && !view.eligible && (
                  <span id="seal-why" className="text-sm text-ink-muted">
                    {t('seal.activate.disabled', { count: unmet.length })}
                  </span>
                )}
                {view.active && (
                  <>
                    <p className="font-bold text-ok">{t('seal.activate.active')}</p>
                    <ConfirmButton
                      label={t('seal.withdraw')}
                      title={t('seal.withdrawTitle')}
                      body={t('seal.withdrawMessage')}
                      confirm={t('seal.withdraw')}
                      cancel={t('statement.cancel')}
                      onConfirm={() => change('withdraw')}
                    />
                  </>
                )}
              </div>
            ) : (
              <p>{t('seal.readOnly')}</p>
            )}
          </section>

          <section aria-labelledby="sello-vista" className="statement-step">
            <h3 id="sello-vista">{t('seal.preview.title')}</h3>
            {!view.eligible && !view.active && (
              <p className="seal-notice">{t('seal.preview.notReady', { count: unmet.length })}</p>
            )}
            <div className="seal-controls">
              <Segmented<SealLang>
                label={t('seal.preview.language')}
                value={(selected ?? 'es') as SealLang}
                options={view.languages.supported.map((code) => ({
                  id: code,
                  lang: code,
                  label: `${t(`statement.languages.${code}`)}${view.languages.detected.includes(code) ? ` ${t('seal.preview.detected')}` : ''}`,
                }))}
                onChange={setLang}
              />
              <Segmented<SealTheme>
                label={t('seal.preview.theme')}
                value={theme}
                options={THEMES.map((id) => ({ id, label: t(`seal.preview.themes.${id}`) }))}
                onChange={setTheme}
              />
              <Segmented<SealSize>
                label={t('seal.preview.size')}
                value={size}
                options={SIZES.map(({ id, px }) => ({
                  id,
                  label: `${t(`seal.preview.sizes.${id}`)} (${px} px)`,
                }))}
                onChange={setSize}
              />
            </div>
            <div className="seal-stage" data-theme={theme}>
              <figure>
                <Badge siteId={siteId} lang={selected} theme={theme} size={size} alt={alt} />
                <figcaption>{t(`seal.preview.sizes.${size}`)}</figcaption>
              </figure>
              <div className="seal-stage-row">
                {STATES.map((state) => (
                  <figure key={state}>
                    <Badge siteId={siteId} lang={selected} theme={theme} size="s" state={state} alt={alt} />
                    <figcaption>{t(`seal.preview.states.${state}`)}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
            <p className="text-sm font-bold">{t('seal.preview.footer')}</p>
            <div className="seal-footer" data-theme={theme}>
              <p className="seal-footer-links">{t('seal.preview.footerLinks')}</p>
              <Badge siteId={siteId} lang={selected} theme={theme} size={size} alt={alt} />
              <p className="seal-footer-copy">© {site?.name}</p>
            </div>
          </section>

          <section aria-labelledby="sello-poner" className="statement-step">
            <h3 id="sello-poner">{t('seal.embed.title')}</h3>
            {!view.active || !view.delivery ? (
              <p className="text-ink-muted">{t('seal.embed.locked')}</p>
            ) : (
              <ul className="statement-options">
                <li>
                  <h4 className="font-bold">{t('seal.embed.wordpress.title')}</h4>
                  {view.wordpress ? (
                    <>
                      <p>{t('seal.embed.wordpress.how')}</p>
                      <p>
                        <a
                          href={`${(site?.url ?? '').replace(/\/?$/, '/')}wp-admin/admin.php?page=sitenorma`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('seal.embed.wordpress.open')}
                          <span className="sr-only"> ({t('app.newTab')})</span>
                        </a>
                      </p>
                    </>
                  ) : (
                    <p className="text-ink-muted">
                      {t('seal.embed.wordpress.none')}{' '}
                      <Link to="/integrations">{t('seal.embed.wordpress.connect')}</Link>
                    </p>
                  )}
                </li>
                <li>
                  <h4 className="font-bold">{t('seal.embed.html.title')}</h4>
                  <p>{t('seal.embed.html.hint')}</p>
                  <ol className="list-decimal pl-5 text-sm">
                    {(['one', 'two', 'three'] as const).map((key) => (
                      <li key={key}>{t(`seal.embed.html.steps.${key}`)}</li>
                    ))}
                  </ol>
                  <Snippet
                    label={t('seal.embed.html.label')}
                    hint={t('seal.embed.html.snippetHint', {
                      language: t(`statement.languages.${selected ?? 'es'}`),
                      theme: t(`seal.preview.themes.${theme}`),
                      size: t(`seal.preview.sizes.${size}`),
                    })}
                    value={view.delivery.snippet}
                  />
                </li>
              </ul>
            )}
          </section>

          <section aria-labelledby="sello-verificar" className="statement-step">
            <h3 id="sello-verificar">{t('seal.verify.title')}</h3>
            <p>{t('seal.verify.intro')}</p>
            {view.delivery ? (
              <p>
                <a href={`${view.delivery.page}?lang=${pageLang}`} target="_blank" rel="noopener noreferrer">
                  {t('seal.verify.open')}
                  <span className="sr-only"> ({t('app.newTab')})</span>
                </a>
              </p>
            ) : (
              <p className="text-ink-muted">{t('seal.verify.locked')}</p>
            )}
            <ul className="list-disc pl-5">
              {(['renews', 'expires', 'suspends', 'live'] as const).map((key) => (
                <li key={key}>{t(`seal.verify.${key}`, { days: view.rules.validDays })}</li>
              ))}
            </ul>
            {view.evidence.scanDate && (
              <p className="text-sm">
                {t('seal.verify.lastReview', { date: date(view.evidence.scanDate) })}
                {view.expiresAt && ` ${t('seal.verify.validUntil', { date: date(view.expiresAt) })}`}
              </p>
            )}
          </section>
        </>
      )}
    </section>
  );
}
